import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';

import { useStore } from '../../../hooks';
import useReorderSelectedAnnotations from '../../../hooks/useReorderSelectedAnnotations';
import {
  BringForwardIcon,
  BringToFrontIcon,
  SendBackwardIcon,
  SendToBackIcon,
} from './LayerOrderIcons';
import SidebarPanelButton from '../SidebarPanelButton';
import {
  FLOATING_TOOLBAR_ICON_SIZE,
  StyledLayerOrderCompactButton,
  StyledLayerOrderCompactGroup,
} from './LayerOrderControls.styled';

const SIDEBAR_ICON_SIZE = 20;
const COMPACT_ICON_SIZE = FLOATING_TOOLBAR_ICON_SIZE;

const LAYER_ACTIONS = [
  {
    direction: 'forward',
    titleKey: 'bringForwardTitle',
    labelKey: 'bringForwardShort',
    Icon: SendBackwardIcon,
  },
  {
    direction: 'backward',
    titleKey: 'sendBackwardTitle',
    labelKey: 'sendBackwardShort',
    Icon: BringForwardIcon,
  },
  {
    direction: 'front',
    titleKey: 'bringToFrontTitle',
    labelKey: 'bringToFrontShort',
    Icon: SendToBackIcon,
  },
  {
    direction: 'back',
    titleKey: 'sendToBackTitle',
    labelKey: 'sendToBackShort',
    Icon: BringToFrontIcon,
  },
];

const GRID_SIDEBAR_ICON_SIZE = 18;

const LAYER_SIDEBAR_ROW_GROUPS = [
  ['forward', 'backward'],
  ['front', 'back'],
];

const LayerOrderControls = ({ variant = 'sidebar', className = '' }) => {
  const { t } = useStore();
  const { reorder, layerOrderState } = useReorderSelectedAnnotations();

  const disabledHint = useMemo(() => {
    if (layerOrderState.stackCount < 2) {
      return t('layerOrderDisabledSingleLayer');
    }
    if (!layerOrderState.hasSelection) {
      return t('layerOrderDisabledNoSelection');
    }
    if (layerOrderState.hasLockedSelection) {
      return t('layerOrderDisabledLocked');
    }
    return t('layerOrderDisabledAtLimit');
  }, [
    layerOrderState.hasLockedSelection,
    layerOrderState.hasSelection,
    layerOrderState.stackCount,
    t,
  ]);

  const getButtonTitle = useCallback(
    (titleKey, direction) => {
      const base = t(titleKey);
      if (layerOrderState.directions[direction]) {
        return base;
      }
      return `${base} — ${disabledHint}`;
    },
    [disabledHint, layerOrderState.directions, t],
  );

  const handleKeyDown = useCallback(
    (event, direction, disabled) => {
      if (disabled) {
        return;
      }
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        reorder(direction);
      }
    },
    [reorder],
  );

  const renderButton = (direction, titleKey, labelKey, Icon, ButtonWrap, iconSize) => {
    const disabled = !layerOrderState.directions[direction];
    const title = getButtonTitle(titleKey, direction);

    if (variant === 'sidebar') {
      return (
        <div
          key={direction}
          className="respark_inspector-option-row-cell"
        >
          <SidebarPanelButton
            className={`respark_layer-order-btn respark_layer-order-btn--${direction}${className ? ` ${className}` : ''}`}
            Icon={Icon}
            label={t(labelKey)}
            title={title}
            disabled={disabled}
            onClick={() => reorder(direction)}
            onKeyDown={(event) => handleKeyDown(event, direction, disabled)}
          />
        </div>
      );
    }

    return (
      <ButtonWrap
        key={direction}
        type="button"
        className={`respark_layer-order-btn respark_layer-order-btn--${direction}${disabled ? ' respark_layer-order-btn--disabled' : ''}${className ? ` ${className}` : ''}`}
        aria-disabled={disabled}
        title={title}
        aria-label={title}
        disabled={disabled}
        onClick={() => !disabled && reorder(direction)}
      >
        <Icon size={iconSize} />
      </ButtonWrap>
    );
  };

  if (variant === 'compact') {
    return (
      <StyledLayerOrderCompactGroup
        className={`respark_layer-order-controls respark_layer-order-controls--compact${className ? ` ${className}` : ''}`}
        role="group"
        aria-label={t('layerOrderTitle')}
      >
        {LAYER_ACTIONS.map(({ direction, titleKey, labelKey, Icon }) =>
          renderButton(
            direction,
            titleKey,
            labelKey,
            Icon,
            StyledLayerOrderCompactButton,
            COMPACT_ICON_SIZE,
          ),
        )}
      </StyledLayerOrderCompactGroup>
    );
  }

  return (
    <div
      className={`respark_layer-order-sidebar-grid${className ? ` ${className}` : ''}`}
      role="group"
      aria-label={t('layerOrderTitle')}
    >
      {LAYER_SIDEBAR_ROW_GROUPS.map((rowDirections) => (
        <div
          key={rowDirections.join('-')}
          className="respark_inspector-option-row respark_layer-order-sidebar-row"
        >
          {rowDirections.map((direction) => {
            const action = LAYER_ACTIONS.find((item) => item.direction === direction);
            if (!action) {
              return null;
            }
            return renderButton(
              action.direction,
              action.titleKey,
              action.labelKey,
              action.Icon,
              null,
              SIDEBAR_ICON_SIZE,
            );
          })}
        </div>
      ))}
    </div>
  );
};

LayerOrderControls.propTypes = {
  variant: PropTypes.oneOf(['sidebar', 'compact']),
  className: PropTypes.string,
};

export default LayerOrderControls;
